<template>
  <div v-if="showPermissionRequest" class="interaction-prompt" @click="requestPermission">
    <div class="prompt-content">
      <p>🔔 Enable Push Notifications</p>
      <small>Get notifications even when the app is closed</small>
      <button class="enable-btn">Enable</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from "vue";
import {
  updateFCMToken,
  checkTokenValidity,
  onMessageListener,
} from "@/services/firebase";
import axios from "@/api";
import { getUser } from "@/utils/permission";
import { getMessaging, getToken } from "firebase/messaging";

export default {
  name: "PushNotificationSetup",
  setup() {
    const showPermissionRequest = ref(false);
    const tokenRefreshInterval = ref(null);
    const keepAliveInterval = ref(null);
    const visibilityHandler = ref(null);
    const messageHandler = ref(null);

    onMounted(() => {
      checkNotificationSupport();
      setupTokenAutoRefresh();
      setupSilentUpdates(); // ✅ Vercel update notifications ကို ဖျောက်ရန်
    });

    onBeforeUnmount(() => {
      if (tokenRefreshInterval.value) {
        clearInterval(tokenRefreshInterval.value);
      }
      if (keepAliveInterval.value) {
        clearInterval(keepAliveInterval.value);
      }
      if (visibilityHandler.value) {
        document.removeEventListener("visibilitychange", visibilityHandler.value);
      }
      if (messageHandler.value && navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", messageHandler.value);
      }
    });

    // ✅ Service Worker update ကို silent ဖြစ်အောင် လုပ်မယ်
    const setupSilentUpdates = () => {
      if ("serviceWorker" in navigator) {
        // Service Worker က message ပို့လာရင် handle လုပ်မယ်
        messageHandler.value = (event) => {
          if (event.data && event.data.type === "SKIP_WAITING") {
            console.log("Skipping waiting for SW update");
            // update notification ကို မပြစေရဘူး
          }
        };

        navigator.serviceWorker.addEventListener("message", messageHandler.value);

        // Service Worker update ကို background မှာလုပ်မယ်
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          console.log("Service Worker controller changed, reloading silently...");
          // page reload လုပ်စရာမလိုပဲ background မှာ update လုပ်မယ်
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: "SILENT_UPDATE",
            });
          }
        });

        // background မှာ update စစ်မယ် (နေ့စဉ် ၁ ကြိမ်)
        setTimeout(() => {
          checkForUpdates();
        }, 5000);
      }
    };

    // Update checking ကို background မှာလုပ်မယ်
    const checkForUpdates = async () => {
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;

            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                console.log("New SW installed, activating silently...");
                // ချက်ချင်း activate လုပ်မယ်
                newWorker.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });

          // background မှာ update စစ်မယ် (နေ့စဉ် ၁ ကြိမ်)
          setInterval(() => {
            registration.update();
          }, 24 * 60 * 60 * 1000); // ၂၄ နာရီတိုင်း
        }
      }
    };

    const checkNotificationSupport = () => {
      if ("Notification" in window && "serviceWorker" in navigator) {
        if (Notification.permission === "default") {
          showPermissionRequest.value = true;
        } else if (Notification.permission === "granted") {
          setupPushNotifications();
        }
      }
    };

    // ✅ Token Auto Refresh Setup

    const setupTokenAutoRefresh = async () => {
      try {
        const user = await getUser();
        if (user && user.id) {
          // Initial token check
          await checkTokenValidity(user.id);

          // Token refresh every 1 hour instead of 4 hours
          tokenRefreshInterval.value = setInterval(async () => {
            const currentUser = await getUser();
            if (currentUser && currentUser.id) {
              await checkTokenValidity(currentUser.id);

              // Force service worker to stay alive
              if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                  type: "PING",
                  timestamp: Date.now(),
                });
              }
            }
          }, 60 * 60 * 1000); // 1 hour
        }
      } catch (error) {
        console.error("Failed to setup token auto-refresh:", error);
      }
    };

    const requestPermission = async () => {
      try {
        const permission = await Notification.requestPermission();

        if (permission === "granted") {
          showPermissionRequest.value = false;
          await setupPushNotifications();
        }
      } catch (error) {
        console.error("Permission error:", error);
      }
    };

    const setupPushNotifications = async () => {
      try {
        const registration = await registerServiceWorker();

        const user = await getUser();
        if (user && user.id) {
          const token = await updateFCMToken(user.id, true);

          if (token) {
            await subscribeToServer(token);
            setupForegroundListener();

            // ✅ ပိုပြီး မကြာခဏ Keep-alive ပို့မယ် (၂ မိနစ်တိုင်း)
            if (keepAliveInterval.value) {
              clearInterval(keepAliveInterval.value);
            }

            keepAliveInterval.value = setInterval(() => {
              if (navigator.serviceWorker.controller) {
                // Service Worker ကို ping ပို့ပြီး နိုးထစေမယ်
                const messageChannel = new MessageChannel();

                messageChannel.port1.onmessage = (event) => {
                  if (event.data && event.data.type === "PONG") {
                    console.log("✅ Service Worker is alive");
                  }
                };

                navigator.serviceWorker.controller.postMessage(
                  {
                    type: "PING",
                    timestamp: Date.now(),
                  },
                  [messageChannel.port2]
                );

                // Firebase Token ကိုလည်း ပြန်စစ်မယ်
                checkTokenValidity(user.id);
              }
            }, 2 * 60 * 1000); // ၂ မိနစ်တိုင်း
          }
        }
      } catch (error) {
        console.error("Push setup error:", error);
      }
    };

    const registerServiceWorker = async () => {
      if ("serviceWorker" in navigator) {
        try {
          // Check existing registration
          let registration = await navigator.serviceWorker.getRegistration();

          if (registration) {
            console.log("✅ Service Worker already registered");
            // Update check
            await registration.update();
          } else {
            // Register new service worker
            registration = await navigator.serviceWorker.register(
              "/firebase-messaging-sw.js",
              {
                scope: "/",
                updateViaCache: "none",
              }
            );
            console.log("✅ Service Worker registered:", registration);
          }

          // ✅ ဒီမှာ registration ကို အသုံးပြုထားကြောင်း သေချာအောင်လုပ်မယ်
          console.log("Registration status:", registration ? "active" : "inactive");

          // Wait for ready
          await navigator.serviceWorker.ready;

          // Firebase token ရယူမယ်
          const messaging = getMessaging();
          const token = await getToken(messaging, {
            vapidKey:
              "BPsFpCtyxykMZEpDJq2cMIf53kHt1nlEk_9Y3glFocjAMUoFlEiZZETGFz524AHa1gL2ytnKotNh4LZjtHA1Wy4",
            serviceWorkerRegistration: registration,
          });

          console.log("✅ FCM Token:", token);

          // ✅ ဒီမှာလည်း registration ကို return ပြန်ထားတယ်
          return registration;
        } catch (error) {
          console.error("❌ Service Worker registration failed:", error);

          // Fallback
          try {
            const messaging = getMessaging();
            const token = await getToken(messaging, {
              vapidKey:
                "BPsFpCtyxykMZEpDJq2cMIf53kHt1nlEk_9Y3glFocjAMUoFlEiZZETGFz524AHa1gL2ytnKotNh4LZjtHA1Wy4",
            });
            console.log("✅ Got token with default registration:", token);
            return null;
          } catch (fbError) {
            console.error("❌ Firebase fallback failed:", fbError);
            throw fbError;
          }
        }
      }
    };

    const setupForegroundListener = () => {
      onMessageListener((payload) => {
        console.log("🔥 Foreground message received:", payload);

        // Show notification even in foreground
        const data = payload.data || payload.notification?.data || {};

        // 🔥 ORDER NOTIFICATION ONLY
        if (
          data.type === "new_order" ||
          data.type === "order_accepted" ||
          data.order_id
        ) {
          // Use browser notification instead of toast
          if (Notification.permission === "granted") {
            new Notification(`🛵 New Order #${data.order_code || data.order_id}`, {
              body: data.body || "A new delivery order has been assigned to you.",
              icon: "/icon-192x192.png",
              badge: "/badge-72x72.png",
              tag: `order-${data.order_id}`,
              vibrate: [200, 100, 200],
              requireInteraction: true,
              silent: false,
            });
          }
        } else {
          console.log("Ignoring non-order foreground message:", data);
        }
      });
    };

    const subscribeToServer = async (token) => {
      try {
        const response = await axios.post("/push/subscribe", {
          endpoint: token,
          keys: {
            auth: "AUTH_KEY_PLACEHOLDER",
            p256dh: "P256DH_KEY_PLACEHOLDER",
          },
        });

        if (response.data.success) {
          console.log("✅ Successfully subscribed to push notifications");
          localStorage.setItem("push_subscribed", "true");
          localStorage.setItem("push_subscribed_at", Date.now().toString());
        }
      } catch (error) {
        console.error("❌ Subscription error:", error);
      }
    };

    return {
      showPermissionRequest,
      requestPermission,
    };
  },
};
</script>

<style scoped>
.interaction-prompt {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: white;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  max-width: 250px;
  cursor: pointer;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.prompt-content {
  text-align: center;
}

.prompt-content p {
  margin: 0 0 8px 0;
  font-weight: bold;
  color: #333;
}

.prompt-content small {
  color: #666;
  display: block;
  margin-bottom: 12px;
  font-size: 12px;
}

.enable-btn {
  background: #4caf50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: background 0.2s;
}

.enable-btn:hover {
  background: #45a049;
}
</style>
